import type { Session, User } from "@supabase/supabase-js";
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

import { createClient } from "~/lib/supabase";

const supabase = createClient();

interface AuthContextValue {
	user: User | null;
	session: Session | null;
	loading: boolean;
	signInWithEmail: (
		email: string,
		password: string,
	) => ReturnType<typeof supabase.auth.signInWithPassword>;
	signUp: (email: string, password: string) => ReturnType<typeof supabase.auth.signUp>;
	signInWithOAuth: (provider: "google") => ReturnType<typeof supabase.auth.signInWithOAuth>;
	signOut: () => ReturnType<typeof supabase.auth.signOut>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
	const [user, setUser] = useState<User | null>(null);
	const [session, setSession] = useState<Session | null>(null);
	const [loading, setLoading] = useState(true);

	useEffect(() => {
		supabase.auth.getSession().then(({ data: { session } }) => {
			setSession(session);
			setUser(session?.user ?? null);
			setLoading(false);
		});

		const {
			data: { subscription },
		} = supabase.auth.onAuthStateChange((_event, session) => {
			setSession(session);
			setUser(session?.user ?? null);
		});

		return () => subscription.unsubscribe();
	}, []);

	const signInWithEmail = useCallback(async (email: string, password: string) => {
		return supabase.auth.signInWithPassword({ email, password });
	}, []);

	const signUp = useCallback(async (email: string, password: string) => {
		return supabase.auth.signUp({ email, password });
	}, []);

	const signInWithOAuth = useCallback(async (provider: "google") => {
		return supabase.auth.signInWithOAuth({
			provider,
			options: { redirectTo: window.location.origin },
		});
	}, []);

	const signOut = useCallback(async () => {
		return supabase.auth.signOut();
	}, []);

	const value = useMemo(
		() => ({ user, session, loading, signInWithEmail, signUp, signInWithOAuth, signOut }),
		[user, session, loading, signInWithEmail, signUp, signInWithOAuth, signOut],
	);

	return <AuthContext value={value}>{children}</AuthContext>;
}

export function useAuth() {
	const context = useContext(AuthContext);
	if (!context) {
		throw new Error("useAuth must be used within an AuthProvider");
	}
	return context;
}
